import React, {useState, useEffect, useContext} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import {Loader} from '../loaders/loader';
import {
  OrganizationContext,
  ORG_TYPES,
  ORG_STRUCTURE_MODES
} from '../../enhancers/organization-enhancer';
import {ReferenceBox, PopperBox} from '../popovers/hint/styles';
import TriangleIcon from '../icons/triangle.svg';
import {BASE_TEXT} from '../../style/typography';
import {WHITE, CHARCOAL, ASH, FOCUS_BLUE, TARMAC} from '../../style/colors';
import SearchTextInput from '../inputs/search';
import {debounce} from '../../utils/debounce';
import {ApolloContext} from '../../enhancers/graphql-enhancer';
import {organizationsQuery} from '../../../data/shared/queries';
import {flattenEdges} from '../../../shared/utils/graphql';
import DropArrow from '../icons/orgs/drop_arrow.svg';
import OrgIcon from '../icons/orgs/org_icon.svg';
import {VCS_ORG_PROVIDERS} from './info-box';
import {truncateText} from '../../utils/truncate-text';
import PopoverWithAnchor from '../popovers/base-v2';
import {TOP} from '../../constants/placements';
import modifyOrgList from '../../utils/orgs/modify-org-list';
import {generateNumbers} from '../../utils/number';

const Container = glamorous.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
});

const TriangleShape = glamorous.div({
  width: 8,
  height: 5,
  margin: '0 14px 20px 10px',
  borderRadius: '0.5'
});

const OrgShape = glamorous(OrgIcon)({
  width: 14,
  height: 14
});

const ItemContainer = glamorous.div(
  {
    ...BASE_TEXT,
    display: 'flex',
    alignItems: 'center',
    height: 40,
    margin: '0 10px 0 0',
    padding: '7px 2px 7px 8px',
    borderRadius: 2,
    border: 'solid 1px #e4e4e4',
    backgroundColor: WHITE,
    cursor: 'pointer',
    justifyContent: 'space-between'
  },
  ({minWidth}) => ({
    minWidth: minWidth || 225
  })
);

const ListContainer = glamorous.div({
  width: '100%',
  maxHeight: 230,
  overflowX: 'hidden',
  '::-webkit-scrollbar': {
    width: 7,
    height: 5
  },

  '::-webkit-scrollbar-thumb': {
    borderRadius: 5,
    backgroundColor: 'rgba(0,0,0,.5)',
    boxShadow: ' 0 0 1px rgba(255,255,255,.5)'
  }
});

const ListItem = glamorous.div(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 34,
    padding: 10,
    paddingLeft: 7
  },
  ({hasChildOrgs}) => ({
    '&:hover': {
      backgroundColor: '#F3F8FB',
      cursor: hasChildOrgs && 'pointer'
    }
  })
);

const Organization = glamorous.div({
  margin: '0 5px',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 20px auto)',
  gridColumnGap: 8
});

const SelectedOrganization = glamorous.div({
  margin: '0 5px',
  display: 'grid',
  gridTemplateColumns: '20px auto',
  gridColumnGap: 8
});

const OrgImage = glamorous.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
});

const OrgName = glamorous.div(
  {
    textAlign: 'left',
    fontSize: 13,
    lineHeight: 1.3,
    padding: 0,
    color: CHARCOAL,
    marginRight: 3,
    width: 'max-content',
    maxWidth: 190,
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  ({selected}) => ({
    '&:hover': {
      textDecoration: !selected && 'underline',
      cursor: !selected && 'pointer'
    }
  })
);

const OrgCount = glamorous.div({
  fontSize: 13,
  lineHeight: 1.3,
  padding: 0,
  color: CHARCOAL,
  marginLeft: -15
});

const AllOrgContainer = glamorous.div(
  {
    color: CHARCOAL,
    paddingLeft: 8,
    fontSize: 13
  },
  ({selected}) => ({
    '&:hover': {
      textDecoration: !selected && 'underline',
      cursor: !selected && 'pointer'
    }
  })
);

const NoOrgsFound = glamorous.div({
  padding: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const DownArrowIcon = glamorous(DropArrow)({
  minWidth: 18,
  minHeight: 18,
  ' path': {
    fill: TARMAC
  },
  backgroundColor: WHITE,
  cursor: 'pointer',
  borderRadius: '50%'
});

const UpArrowIcon = glamorous(DropArrow)({
  minWidth: 18,
  minHeight: 18,
  transform: 'rotate(180deg)',
  ' path': {
    fill: FOCUS_BLUE
  },
  backgroundColor: WHITE,
  cursor: 'pointer',
  borderRadius: '50%'
});

let Manager = null;
let Reference = null;
let Popper = null;

const OrganizationPicker = ({customStyle = {}, minWidth}) => {
  const organizationContext = useContext(OrganizationContext);
  const client = useContext(ApolloContext);
  const [ready, setReady] = useState(false);
  const [showNotifyMenu, setNotifyMenu] = useState(false);
  const [container, setContainer] = useState();
  const [searchText, setSearchText] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState('');
  const {
    totalOrgsCount,
    loading,
    list: orgsList,
    currentOrg,
    setOrg,
    orgStructureMode
  } = organizationContext;
  const orgSelected = currentOrg && currentOrg.id && currentOrg.slug !== ORG_TYPES.ALL.slug;
  const urlParams = new URLSearchParams(location && location.search);

  useEffect(() => {
    import(/* webpackChunkName: "react-popper" */ 'react-popper').then(module => {
      Manager = module.Manager;
      Reference = module.Reference;
      Popper = module.Popper;
      setReady(true);
    });
  }, []);

  useEffect(() => {
    setOrgList(modifyOrgList(orgsList, searchText, currentOrg));
    setEndCursor(organizationContext.endCursor);
    setHasNextPage(organizationContext.hasNextPage);
  }, [orgsList, showNotifyMenu]);

  const getVcsIcon = element => {
    return VCS_ORG_PROVIDERS[element.vcsProvider.toLowerCase()] || null;
  };

  const handleClick = () => {
    if (!showNotifyMenu) {
      document.addEventListener('click', handleDismiss, {capture: true});
    } else {
      setSearchText('');
      setOrgList(modifyOrgList(orgsList, searchText, currentOrg));
      setEndCursor(organizationContext.endCursor);
      setHasNextPage(organizationContext.hasNextPage);
    }
    setNotifyMenu(!showNotifyMenu);
  };

  const handleDismiss = event => {
    if (!container.contains(event.target)) {
      setNotifyMenu(false);
      setSearchText('');
      setOrgList([]);
      document.removeEventListener('click', handleDismiss, {capture: true});
    }
  };

  const handleMenuClick = org => {
    setOrg(org);
    setNotifyMenu(false);
  };

  const handleInputChange = e => {
    setSearchText(e.target.value);
    searchHandler(e.target.value);
  };

  const searchHandler = debounce(value => {
    getSearchData(value);
  }, 500);

  const getSearchData = searchQuery => {
    client
      .query({
        query: organizationsQuery,
        variables: {query: searchQuery, first: 10, after: '', slug: ''}
      })
      .then(({data, loading}) => {
        if (loading && !data) setLoader(true);

        if (!loading && data) {
          const hasData = data?.currentPrivateCompany;
          const orgs = hasData?.rootOrgs;
          const orgsList = flattenEdges(orgs).filter(org => org);
          const flatData = hasData?.totalOrgsCount > 1 ? [...orgsList] : orgsList;
          setOrgList(modifyOrgList(flatData, searchQuery, currentOrg));
          setEndCursor(orgs.pageInfo.endCursor);
          setHasNextPage(orgs.pageInfo.hasNextPage);
        }
      });
  };

  const onLoadMore = () => {
    client
      .query({
        query: organizationsQuery,
        variables: {query: searchText, first: 10, after: endCursor, slug: urlParams.get('org')}
      })
      .then(resp => {
        if (resp.loading && !resp.data) setLoader(true);

        if (!resp.loading && resp.data) {
          const hasData = resp.data?.currentPrivateCompany;
          const orgs = hasData?.rootOrgs;
          const newList = flattenEdges(orgs).filter(org => org);
          const flatData = [...orgList, ...newList];
          setOrgList(modifyOrgList(flatData, searchText, currentOrg));
          setEndCursor(orgs.pageInfo.endCursor);
          setHasNextPage(orgs.pageInfo.hasNextPage);
        }
      });
  };

  const onScrollMore = ({currentTarget}) => {
    if (currentTarget.scrollTop + currentTarget.clientHeight >= currentTarget.scrollHeight - 10) {
      if (hasNextPage && endCursor) onLoadMore();
    }
  };

  if (loading) {
    return (
      <Container style={{marginRight: 15, ...customStyle}}>
        <Loader w={minWidth || 225} h={42} animate />
      </Container>
    );
  }

  if (!loading && totalOrgsCount > 1 && ready) {
    return (
      <div style={{marginBottom: 25, ...customStyle}}>
        <Container innerRef={el => setContainer(el)}>
          <Manager>
            <Reference>
              {({ref}) => (
                <ReferenceBox innerRef={ref} onClick={handleClick}>
                  <ItemContainer minWidth={minWidth}>
                    {orgSelected ? (
                      <SelectedOrganization>
                        <OrgImage>
                          {currentOrg.vcsProvider ? getVcsIcon(currentOrg) : <OrgShape />}
                        </OrgImage>
                        {currentOrg.name && <OrgName selected>{currentOrg.name}</OrgName>}
                      </SelectedOrganization>
                    ) : (
                      <AllOrgContainer selected>{'All Organizations'}</AllOrgContainer>
                    )}
                    <TriangleShape>
                      <TriangleIcon />
                    </TriangleShape>
                  </ItemContainer>
                </ReferenceBox>
              )}
            </Reference>
            {showNotifyMenu && (
              <Popper
                placement="bottom"
                modifiers={{
                  flip: {enabled: false},
                  preventOverflow: {escapeWithReference: true}
                }}
              >
                {({ref, style, placement}) => (
                  <PopperBox
                    innerRef={ref}
                    style={{
                      ...style,
                      minWidth: minWidth || 225,
                      width: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      margin: '24px 2px 0 167px',
                      padding: 0,
                      borderRadius: 2,
                      boxShadow: '0 11px 20px 0 rgba(0, 0, 0, 0.05)',
                      border: `1px solid ${ASH}`,
                      backgroundColor: WHITE,
                      transform: 'translate3d(-168px, 17px, 0px)'
                    }}
                    data-placement={placement}
                  >
                    <SearchTextInput
                      placeholder="Search"
                      id="orgs"
                      name="orgs"
                      value={searchText}
                      onChange={e => handleInputChange(e)}
                      inputBoxStyle={{
                        height: 40,
                        fontSize: 12,
                        borderWidth: '0 0 1px 0',
                        borderColor: '#e5e5e5',
                        ':focus': {
                          borderColor: '#e5e5e5'
                        }
                      }}
                      showClearIcon={false}
                      iconType="small"
                      style={{width: '100%'}}
                    />
                    <ListContainer onScroll={e => onScrollMore(e)}>
                      {loader ? (
                        <>
                          {generateNumbers(1, 7).map(_ => (
                            <Loader w={minWidth || 225} h={42} key={_} animate />
                          ))}
                        </>
                      ) : null}
                      {orgList.length !== 0 && !loader ? (
                        <>
                          {orgList.map(org => {
                            return (
                              <DropDownItem
                                key={`org-${org.id}`}
                                item={org}
                                onClick={org => {
                                  handleMenuClick(org);
                                }}
                                totalOrgsCount={totalOrgsCount}
                                orgStructureMode={orgStructureMode}
                                getVcsIcon={getVcsIcon}
                              />
                            );
                          })}
                        </>
                      ) : (
                        <NoOrgsFound>{'No Organizations Found!'}</NoOrgsFound>
                      )}
                    </ListContainer>
                  </PopperBox>
                )}
              </Popper>
            )}
          </Manager>
        </Container>
      </div>
    );
  } else return null;
};

//Each drop down item in the list
export const DropDownItem = ({item, onClick, totalOrgsCount, orgStructureMode, getVcsIcon}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildOrgs = item.childOrgs && item.childOrgs.length > 0;

  useEffect(() => {
    setIsOpen(item.isOpen ? item.isOpen : false);
  }, [item.isOpen]);

  const toggleOpen = event => {
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      {item.id === ORG_TYPES.ALL.slug ? (
        <ListItem key={`org-all`} style={{justifyContent: 'flex-start'}}>
          <AllOrgContainer style={{marginRight: 3}} onClick={() => onClick(ORG_TYPES.ALL)}>
            All Organizations
          </AllOrgContainer>
          {orgStructureMode === ORG_STRUCTURE_MODES.vcs && <span>{`(${totalOrgsCount})`}</span>}
        </ListItem>
      ) : (
        <>
          <ListItem key={`org-${item.id}`} onClick={e => toggleOpen(e)} hasChildOrgs={hasChildOrgs}>
            <Organization>
              <OrgImage>{item.vcsProvider ? getVcsIcon(item) : <OrgShape />}</OrgImage>
              {item.name && (
                <PopoverWithAnchor
                  width={140}
                  customStyle={{backgroundColor: CHARCOAL, color: WHITE}}
                  arrowColor={CHARCOAL}
                  padding={10}
                  placement={TOP}
                  activateMode="hover"
                  modifiersOffset={false}
                  hidden={true}
                  anchor={
                    <OrgName onClick={() => onClick(item)}>
                      {truncateText(item.name, 25, '...', true)}
                    </OrgName>
                  }
                >
                  {item.name}
                </PopoverWithAnchor>
              )}

              {hasChildOrgs && <OrgCount>{`(${item.childOrgs.length})`}</OrgCount>}
            </Organization>
            {hasChildOrgs ? (
              isOpen ? (
                <UpArrowIcon onClick={e => toggleOpen(e)} />
              ) : (
                <DownArrowIcon onClick={e => toggleOpen(e)} />
              )
            ) : null}
          </ListItem>
          {isOpen && hasChildOrgs && (
            <div style={{marginLeft: '15px'}}>
              {item.childOrgs.map(child => (
                <DropDownItem
                  key={child.id}
                  item={child}
                  totalOrgsCount={totalOrgsCount}
                  onClick={onClick}
                  getVcsIcon={getVcsIcon}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

DropDownItem.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  totalOrgsCount: PropTypes.number,
  orgStructureMode: PropTypes.string,
  getVcsIcon: PropTypes.func
};

OrganizationPicker.propTypes = {
  customStyle: PropTypes.any
};

export default OrganizationPicker;
